import React, { useState, useMemo } from 'react';
import { Calendar, Clock, X, Check, CheckCircle2 } from 'lucide-react';
import { LeadsStore } from '../../lib/leadsStore';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { DatePopover } from '../ui/date-popover';

// 48 half-hour slots from 12:00 AM → 11:30 PM. Stored as "HH:MM" strings
// (24-hour) so the existing save logic keeps working unchanged. Labels
// render in friendly 12-hour AM/PM form. Matches the reference UX.
const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
      out.push({ value, label });
    }
  }
  return out;
})();

/**
 * AppointmentScheduler — date + time picker that PATCHes lead.appointmentAt.
 * Lives in the lead's Notes section. Single source of truth for "Follow up today."
 *
 * Data model: lead.appointmentAt is an ISO-8601 string (`2026-05-07T14:30:00.000Z`).
 * Empty/null = no appointment scheduled.
 */
export default function AppointmentScheduler({ lead }) {
  const [editing, setEditing] = useState(false);
  const existing = lead?.appointmentAt ? new Date(lead.appointmentAt) : null;

  // Local form state — pre-fill from existing.
  // For the time slot we snap to the nearest 30-min increment so the
  // <select> always has a matching value (otherwise the dropdown would
  // appear blank for legacy data saved at e.g. 14:23).
  const [date, setDate] = useState(existing ? toLocalDateInput(existing) : '');
  const [time, setTime] = useState(existing ? snapToHalfHour(existing) : '10:00');

  // Reset local state if appointment changes externally (e.g. Wingman)
  React.useEffect(() => {
    if (lead?.appointmentAt) {
      const d = new Date(lead.appointmentAt);
      setDate(toLocalDateInput(d));
      setTime(snapToHalfHour(d));
    } else {
      setDate('');
      setTime('10:00');
    }
  }, [lead?.appointmentAt]);

  const display = useMemo(() => {
    if (!existing) return null;
    return formatAppointment(existing);
  }, [existing]);

  const save = async () => {
    if (!date || !time) {
      toast.error('Pick a date and time first');
      return;
    }
    const iso = new Date(`${date}T${time}:00`).toISOString();
    const updated = await LeadsStore.update(lead.id, { appointmentAt: iso });
    if (updated) {
      toast.success(`Appointment set: ${formatAppointment(new Date(iso))}`);
      setEditing(false);
    } else {
      toast.error('Could not save appointment');
    }
  };

  const clear = async () => {
    const updated = await LeadsStore.update(lead.id, { appointmentAt: null });
    if (updated) {
      toast.success('Appointment cleared');
      setEditing(false);
    }
  };

  // "Completed" — used after the LO has made the call. Clears the
  // scheduled appointment so it drops out of the Appointment chip.
  // (We don't keep a separate "completed appointment history" yet;
  // call-log integration via RingCentral will own that view.)
  const markCompleted = async () => {
    const updated = await LeadsStore.update(lead.id, { appointmentAt: null });
    if (updated) toast.success(`${updated.name} — appointment marked complete.`);
    else toast.error('Could not mark complete');
  };

  return (
    <div className="rounded-xl border hairline bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b hairline-soft bg-neutral-50/40">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center shrink-0">
            <Calendar className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Appointment</div>
            <div className="text-[12.5px] font-bold text-neutral-900 leading-tight">
              {display ? display.label : 'No appointment scheduled'}
              {display?.relative && (
                <span className={cn('ml-2 mono text-[10.5px] font-semibold',
                  display.isPast ? 'text-neutral-400' : display.isToday ? 'text-[hsl(var(--kf-gold-deep))]' : 'text-neutral-500')}>
                  {display.relative}
                </span>
              )}
            </div>
          </div>
        </div>

        {!editing && (
          <div className="flex items-center gap-2">
            {existing && (
              <button
                data-testid="appt-completed"
                onClick={markCompleted}
                title="Mark this appointment as completed — the lead will drop out of the Appointment view."
                className="h-8 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-700 hover:border-neutral-400 transition flex items-center gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Completed
              </button>
            )}
            <button
              data-testid="appt-edit"
              onClick={() => setEditing(true)}
              className={cn(
                'h-8 px-3 rounded-md text-[12px] font-semibold transition flex items-center gap-1.5',
                existing
                  ? 'border hairline bg-white text-neutral-700 hover:border-neutral-400'
                  : 'btn-gold'
              )}
            >
              <Clock className="h-3.5 w-3.5" /> {existing ? 'Reschedule' : 'Schedule'}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="px-4 py-3.5 space-y-3 rise-in">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Date</label>
              <div className="mt-1">
                <DatePopover
                  testId="appt-date"
                  value={date}
                  onChange={setDate}
                  min={toLocalDateInput(new Date())}
                  placeholder="Pick a date"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Time</label>
              <select
                data-testid="appt-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-lg bg-white border hairline text-[13px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900 appearance-none cursor-pointer"
              >
                {TIME_SLOTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {existing && (
              <button
                data-testid="appt-clear"
                onClick={clear}
                className="h-8 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-red-600 hover:border-red-300 hover:bg-red-50 flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setEditing(false)}
                className="h-8 px-3 rounded-md text-[12px] font-semibold text-neutral-500 hover:text-neutral-900"
              >
                Cancel
              </button>
              <button
                data-testid="appt-save"
                onClick={save}
                className="h-8 px-4 rounded-md btn-ink text-[12px] font-bold flex items-center gap-1.5"
              >
                <Check className="h-3.5 w-3.5" /> Save appointment
              </button>
            </div>
          </div>
          <p className="text-[10.5px] text-neutral-500 leading-relaxed">
            We'll add this lead to your <span className="font-semibold">Today's follow-ups</span> and remind you 15 minutes before the call.
          </p>
        </div>
      )}
    </div>
  );
}

// --- helpers ---
function toLocalDateInput(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
}
function toLocalTimeInput(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
// Snap a Date to the nearest 30-minute slot ("HH:00" or "HH:30"). Used
// when hydrating the time dropdown from a stored appointmentAt that
// might not already sit on a half-hour boundary (e.g. legacy data).
function snapToHalfHour(d) {
  const totalMin = d.getHours() * 60 + d.getMinutes();
  const snapped = Math.round(totalMin / 30) * 30;
  const wrap = snapped % (24 * 60); // wrap midnight if rounded up to 1440
  const h = Math.floor(wrap / 60);
  const m = wrap % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function formatAppointment(d) {
  const TZ = 'America/Phoenix';
  const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ });
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const dKey = dayFmt.format(d);
  const sameDay = dKey === dayFmt.format(now);
  const isTomorrow = dKey === dayFmt.format(tomorrow);
  const time = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  let label;
  if (sameDay) label = `Today, ${time}`;
  else if (isTomorrow) label = `Tomorrow, ${time}`;
  else label = `${d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', weekday: 'short' })}, ${time}`;

  const diffMs = d.getTime() - now.getTime();
  const isPast = diffMs < -60_000;
  const mins = Math.round(diffMs / 60_000);
  let relative = '';
  if (isPast) relative = '(passed)';
  else if (mins < 60) relative = `· in ${mins}m`;
  else if (mins < 60 * 24) relative = `· in ${Math.round(mins / 60)}h`;
  return { label, relative, isPast, isToday: sameDay };
}
